/* eslint-disable @typescript-eslint/naming-convention */
import { Component } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router';
import type { Location } from 'history';
import PropTypes from 'prop-types';

import Locator from '../../../../shared/Locator';
import { window } from '../../../globals';
import ProjectStore from '../../../stores/ProjectStore';
import type { IAsync, Project, Revision } from '../../../types';
import { nuc, openInNewTab, trimString, isProjectInCurrentOrg, isAuthed } from '../../../utils';
import type { IconClassName } from '../../generic/Icon';
import Icon from '../../generic/Icon';
import Tooltip from '../../generic/Tooltip';
import ProjectNavigation from '../overview/ProjectNavigation';
import RevisionDropdown from './RevisionDropdown';
import Flex from '../../generic/Flex';
import StatusBadge from '../settingsv2/general/BadgePanel/StatusBadge';
import BadgeModal from '../settingsv2/general/BadgePanel/BadgeModal';
import type { BadgeType } from '../settingsv2/general/BadgePanel/types';
import { WithDialogContext } from '../../../contexts/DialogContext';
import type { DialogState } from '../../../contexts/DialogContext';
import ProjectActions from '../../../actions/ProjectActions';
import { FeatureFlagRequired } from '../../generic';
import { HYBRID_PROJECTS } from '../../../../shared/featureFlags/flagNames';
import type { Branch, Tag } from '../overview/BranchesDropdown/types';
import BranchesDropdown from '../overview/BranchesDropdown';
import ProjectIcons from './ProjectIcons';
import RevisionAffordances from './RevisionAffordances';

type Props = {
  notifications: any[];
  params: any;
  revision?: Revision;
  location: Location<{ revisionScanId?: string }>;
  dialogContext: DialogState;
  showBranchDropdown?: boolean;
};

type State = {
  // This type represents a bit of legacy tech debt from how we determine whether
  // or not a project is being updated in the old ProjectNavigation component.
  project: IAsync<Project> & { status?: { updating?: boolean } };
  issueMenuOpen: boolean;
  shouldShowBadgeModal: boolean;
  badgeModalTab: BadgeType;
};

/**
 * Helper for selecting a project's available branches.
 */
function getProjectBranches(project?: Project, selected?: string): Branch[] {
  const refs = project?.references ?? [];
  const branchRefs = refs.filter((ref) => ref.type === 'branch');
  return branchRefs.map((ref) => {
    return {
      type: 'branch',
      name: ref.name,
      revisionLocator: ref.revision_id,
      isSelected: ref.name === selected,
      isTracked: project?.tracking_branches?.includes(ref.name),
      isArchived: project?.hidden_branches?.includes(ref.name),
      isDefault: ref.name === project?.default_branch,
    };
  });
}

/**
 * Helper for selecting a project's available tags.
 */
function getProjectTags(project?: Project, selected?: string): Tag[] {
  const refs = project?.references ?? [];
  const tagRefs = refs.filter((ref) => ref.type === 'tag');
  return tagRefs.map((ref) => {
    return {
      type: 'tag',
      name: ref.name,
      revisionLocator: ref.revision_id,
      isSelected: ref.name === selected,
    };
  });
}

class DisplayProjectNav extends Component<Props, State> {
  // eslint-disable-next-line react/static-property-placement
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      project: {
        status: {
          loading: true,
          errors: [],
        },
        data: {},
      },
      issueMenuOpen: false,
      shouldShowBadgeModal: false,
      badgeModalTab: 'licenseShield',
    };
  }

  static defaultProps: Partial<Props> = {
    notifications: [],
  };

  static NavIcon = ({ icon, ...rest }: { icon: IconClassName }) => (
    <span className="nav-icon-tooltip" {...rest}>
      <Icon icon={icon} />
    </span>
  );

  unsubscribeProject: Function;

  componentDidMount() {
    this.unsubscribeProject = (ProjectStore as any).listen(this.onProjectUpdate);
    this.setState({
      project: (ProjectStore as any).getProject(nuc(this.props.params.project_id)) || {
        status: { loading: true },
        data: {},
      },
    });
  }

  componentWillUnmount() {
    if (this.unsubscribeProject) {
      this.unsubscribeProject();
    }
  }

  onProjectUpdate = () => {
    this.setState({
      project: (ProjectStore as any).getProject(nuc(this.props.params.project_id)) || {
        status: { loading: true },
        data: {},
      },
    });
  };

  onNavigateProjectHome = () => {
    window.open(this.state.project.data.url, '_blank', 'noopener=true');
  };

  onNavigateRevisionLink = () => {
    if (!this.props.revision?.link) {
      return;
    }
    this.props.dialogContext.onConfirm({
      title: 'Leaving Fossa',
      body: (
        <div className="modal-link-navigate-text">
          By clicking this link, you will be navigated away from FOSSA to <b>{this.props.revision.link}</b>
        </div>
      ),
      onConfirm: () => openInNewTab(this.props.revision.link),
    });
  };

  getReference = () => {
    if (this.props.params.reference) {
      return this.props.params.reference;
    }

    if (this.state.project && this.state.project.data) {
      return this.state.project.data.default_branch;
    }

    return 'master';
  };

  getRevision = () => {
    if (this.props.params.revision_id) {
      return this.props.params.revision_id;
    }

    if (this.state.project?.data?.last_analyzed_revision) {
      return new Locator(this.state.project.data.last_analyzed_revision).revision.value;
    }

    return '';
  };

  renderTitle = (revisionUrl) => {
    const isLoading = !this.state.project || !this.state.project.data || this.state.project.status.loading;
    const isProjectNotFound = this.state.project?.status?.error?.httpStatusCode === 404;
    const isCurrentOrgProject = isProjectInCurrentOrg(this.state.project.data?.organizationId ?? 0);
    return (
      <div className="project-title-section">
        <h1 className="nav-project-description">
          <span className="animated fadeInLeft">
            {!isLoading ? (
              <span>
                {this.state.project.data.title.length > 50 ? (
                  <Tooltip tooltip={this.state.project.data.title}>
                    <Link
                      style={{ color: '#fff' }}
                      className="project-title-link"
                      to={{
                        pathname:
                          isLoading || isCurrentOrgProject ? revisionUrl : `${revisionUrl}/preview`,
                      }}
                    >
                      {this.state.project.data.title}
                    </Link>
                  </Tooltip>
                ) : (
                  <Link
                    style={{ color: '#fff' }}
                    className="project-title-link"
                    to={{
                      pathname: isLoading || isCurrentOrgProject ? revisionUrl : `${revisionUrl}/preview`,
                    }}
                  >
                    {this.state.project.data.title}
                  </Link>
                )}
                {this.state.project.data && <ProjectIcons
                  project={this.state.project.data}
                  revision={this.props.revision}
                  onNavigateProjectHome={this.onNavigateProjectHome}
                  onNavigateRevisionLink={this.onNavigateRevisionLink}
                />}
                <span className="pull-right">
                  <Tooltip id="status-badge" tooltip="Click for badge embed code" placement="bottom">
                    <div className="inline-block">
                      <Flex direction="column" align="flex-start" gap="8px">
                        <StatusBadge
                          locator={this.props.params.project_id}
                          badgeType="licenseShield"
                          onClick={() => this.setState({ shouldShowBadgeModal: true, badgeModalTab: 'licenseShield' })}
                        />
                        {this.state.project?.data?.securityIssueScanningEnabled && (
                          <StatusBadge
                            locator={this.props.params.project_id}
                            badgeType="securityShield"
                            onClick={() =>
                              this.setState({ shouldShowBadgeModal: true, badgeModalTab: 'securityShield' })}
                          />
                        )}
                      </Flex>
                    </div>
                  </Tooltip>
                  <BadgeModal
                    locator={this.props.params.project_id}
                    isSecurityEnabled={this.state.project?.data?.securityIssueScanningEnabled}
                    shouldShow={this.state.shouldShowBadgeModal}
                    defaultTab={this.state.badgeModalTab}
                    onHide={() => this.setState({ shouldShowBadgeModal: false })}
                  />
                </span>
              </span>
            ) : isProjectNotFound ? (
              <i className="subtitle">Not Found</i>
            ) : (
              <i className="subtitle">loading...</i>
            )}
          </span>
        </h1>
        <Flex justify="flex-start" gap="10px">
          <h1 className="nav-project-description">
            {this.props.params.reference && (this.props.showBranchDropdown || this.props.revision) && (
              <>
                <FeatureFlagRequired flagName={HYBRID_PROJECTS}>
                  <BranchesDropdown
                    isUserAuthenticated={isAuthed()}
                    branches={getProjectBranches(this.state.project.data, this.props.params.reference)}
                    tags={getProjectTags(this.state.project.data, this.props.params.reference)}
                    defaultBranch={this.state.project.data?.default_branch ?? 'master'}
                    onTrackDefaultBranch={() => {
                      // Track the project's default branch.
                      const defaultBranch = this.state.project.data?.default_branch ?? 'master';
                      ProjectActions.pushUpdate(this.state.project.data?.locator, {
                        tracking_branches: [defaultBranch],
                      });
                    }}
                    onClick={(ref) => {
                      const projectLocator = this.state.project.data?.locator;
                      if (!projectLocator) {
                        return;
                      }

                      const revision = Locator.parse(ref.revisionLocator).revision;
                      if (!revision) {
                        return;
                      }

                      const branchURL = `/projects/${nuc(projectLocator)}/refs/${ref.type}/${nuc(ref.name)}/${nuc(revision)}`;
                      this.context.router.push(branchURL);
                    }}
                    onArchiveBranch={(branch) => {
                      const tracking_branches = this.state.project.data?.tracking_branches ?? [];
                      const hidden_branches = this.state.project.data?.hidden_branches ?? [];

                      // Add this branch to the project's hidden_branches field.
                      hidden_branches.push(branch.name);
                      // If the branch is tracked, untrack it (by removing from tracking_branches).
                      if (branch.isTracked) {
                        tracking_branches.splice(tracking_branches.indexOf(branch.name), 1);
                      }

                      // Apply branch updates to the project.
                      ProjectActions.pushUpdate(this.state.project.data?.locator, {
                        tracking_branches,
                        hidden_branches,
                      });
                    }}
                    onUnarchiveBranch={(branch) => {
                      const hidden_branches = this.state.project.data?.hidden_branches ?? [];

                      // Remove the branch from the project's hidden_branches field.
                      hidden_branches.splice(hidden_branches.indexOf(branch.name), 1);

                      // Apply branch updates to the project.
                      ProjectActions.pushUpdate(this.state.project.data?.locator, {
                        hidden_branches,
                      });
                    }}
                    onTrackBranch={(branch) => {
                      const tracking_branches = this.state.project.data?.tracking_branches ?? [];

                      // Add this branch to the project's tracking_branches field.
                      tracking_branches.push(branch.name);

                      // Apply branch updates to the project.
                      ProjectActions.pushUpdate(this.state.project.data?.locator, {
                        tracking_branches,
                      });
                    }}
                    onUntrackBranch={(branch) => {
                      const tracking_branches = this.state.project.data?.tracking_branches ?? [];

                      // Remove the branch from the project's tracking_branches field.
                      tracking_branches.splice(tracking_branches.indexOf(branch.name), 1);

                      // Apply branch updates to the project.
                      ProjectActions.pushUpdate(this.state.project.data?.locator, {
                        tracking_branches,
                      });
                    }}
                  />
                </FeatureFlagRequired>
                <FeatureFlagRequired flagName={HYBRID_PROJECTS} isNotEnabled>
                  <span className="navigation-revision-selector animated fadeInLeft fast">
                    <span
                      style={{
                        position: 'relative',
                        top: '-2px',
                        fontSize: '.8em',
                      }}
                    >
                      <ProjectNavigation
                        project={this.state.project}
                        reference={this.props.params.reference}
                        selectedRevision={this.props.revision}
                        dropdownOnly
                      />
                    </span>
                  </span>
                </FeatureFlagRequired>
              </>
            )}
          </h1>
          <p
            className="animated fadeInDown fast"
            style={{
              zIndex: 1,
              position: 'relative',
              color: '#fff',
              marginBottom: 0,
            }}
          >
            {this.props.params.reference && this.props.revision ? (
              <RevisionDropdown
                project={this.state.project}
                reference={this.props.params.reference}
                referenceType={this.props.params.reference_type}
                selectedRevision={this.props.revision}
              />
            ) : !isLoading && this.state.project.data.description ? (
              trimString(this.state.project.data.description, 175)
            ) : (
              'No description available'
            )}
          </p>
          <RevisionAffordances locator={this.props.revision?.locator || ''} />
        </Flex>
      </div>
    );
  };

  render() {
    const reference = this.getReference();
    const referenceType = this.props.params.reference_type || 'branch';
    const baseUrl = `/projects/${nuc(this.props.params.project_id)}/`;
    const revision = this.getRevision();
    const revisionUrl = `${baseUrl}refs/${referenceType}/${nuc(reference)}/${nuc(revision)}`;
    const helmetTitle = this.state.project.data ? this.state.project.data.title : 'Loading ...';

    return (
      <div>
        <Helmet title={`${helmetTitle} - Projects`} />
        {this.renderTitle(revisionUrl)}
      </div>
    );
  }
}

export default WithDialogContext(DisplayProjectNav);
